import { FC, useEffect, useState } from 'react';
import { CustomButton, CustomInput, Loader } from 'components';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRightIcon } from 'assets/icons';
import TagPreview from './TagPreview';
import { useCreateTagMutation, useLazyGetTagByIdQuery, useLazyGetTagsQuery, useUpdateTagMutation } from '../api';
import { useAppSelector } from 'hooks/typedHooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import routesPath from 'routes/RoutesPath';
import { InputType } from 'components/custom-input.tsx/CustomInput';
import IconPicker from 'components/icon-picker/IconPicker';
import ColorPickerInput from 'components/color-picker/ColorPicker';
import DisplayTagOrder from './DisplayTagOrder';
import Divider from 'components/divider/Divider';
import { selectNextDisplayOrder } from '../tagsSlice';

interface CreateTagProps {
  isEdit?: boolean;
}

const CreateTag: FC<CreateTagProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const nextDisplayOrder = useAppSelector(selectNextDisplayOrder);

  const [name, setName] = useState('');
  const [color, setColor] = useState('#6AA6FF');
  const [icon, setIcon] = useState('');
  const [displayOrder, setDisplayOrder] = useState(nextDisplayOrder?.toString() || '1');
  const [error, setError] = useState('');

  const [createTag, { isLoading }] = useCreateTagMutation();
  const [getTagById, { isLoading: isTagByIdLoading, data: tagData }] = useLazyGetTagByIdQuery();
  const [updateTag, { isLoading: isUpdating }] = useUpdateTagMutation();
  const [getTags] = useLazyGetTagsQuery();

  const { tagId } = useParams();

  useEffect(() => {
    setDisplayOrder(nextDisplayOrder?.toString() || '1');
  }, [nextDisplayOrder]);

  useEffect(() => {
    if (tagId) {
      getTagById({ teamId: currentTeam.id, tagId: tagId })
        .unwrap()
        .then(tag => {
          setName(tag.data.name);
          setColor(tag.data.color);
          setIcon(tag.data.icon);
          setDisplayOrder(tag.data.priority.toString());
        })
        .catch(() => {
          //
        });
    } else {
      getTags({ teamId: currentTeam.id, isActive: true });
    }
  }, [tagId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Item name is required');
      return;
    }

    try {
      if (isEdit && tagId) {
        await updateTag({
          teamId: currentTeam.id,
          tagId: tagId,
          body: {
            name: name.trim(),
            color: color.trim().toUpperCase(),
            icon,
            priority: parseInt(displayOrder, 10),
            isActive: tagData?.data?.isActive
          }
        }).unwrap();
      } else {
        await createTag({
          teamId: currentTeam.id,
          body: {
            name: name.trim(),
            color: color.trim().toUpperCase(),
            icon,
            priority: parseInt(displayOrder, 10)
          }
        }).unwrap();
      }
      navigate(routesPath.TAGS, { replace: true });
    } catch (err) {
      //
    }
  };

  const handleNameChange = (nameValue: string) => {
    setName(nameValue);
    setError('');
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="flex items-center justify-between border-b border-border px-[30px] py-2.5">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-secondary/10">
            <ArrowRightIcon className="h-5 w-5 rotate-180 text-secondary" />
          </button>
          <h1 className="text-xl font-bold text-primaryText">{isEdit ? 'Edit tag' : 'Create tag'}</h1>
        </div>
        <CustomButton
          id="save-tag-button"
          onClick={handleSave}
          className="flex h-10 items-center bg-secondary px-6 text-white"
          disabled={isLoading || isUpdating}
          isLoading={isLoading || isUpdating}>
          {isEdit ? 'Save' : 'Create'}
        </CustomButton>
      </div>
      {isTagByIdLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div className="w-full grow overflow-y-auto p-8">
          <div className="mx-auto w-full rounded-md border border-borderGray py-6 px-8 md:w-[600px]">
            <div className="flex flex-col gap-10">
              <CustomInput
                id="tag-name-input"
                label="Tag name"
                value={name}
                onChange={handleNameChange}
                type={InputType.TEXT}
                placeholder="Enter name"
                isMandatory={true}
                error={error}
                labelClassName="text-[17px] text-headingGray font-semibold"
                maxLength={50}
                showRemainingChar={true}
              />
              <ColorPickerInput label="Tag color" color={color} onChange={setColor} showPresetColors={true} />
              <div className="flex flex-col gap-10 md:flex-row md:items-center md:justify-between md:gap-2">
                <IconPicker label="Icon" selectedIcon={icon} onChange={setIcon} />
                <DisplayTagOrder displayOrder={displayOrder} setDisplayOrder={setDisplayOrder} />
              </div>
            </div>

            <Divider className="my-8" />
            <div className="mb-1.5 text-17px font-semibold text-headingGray">Preview</div>
            <TagPreview name={name} color={color} icon={icon} />
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateTag;
